import React, { useState, useEffect, useRef } from 'react';
import { matchStudent } from '../services/api';

function StudentInput({ data, onChange, errors, setErrors, klassen }) {
  const [matching, setMatching] = useState(false);
  const [matchScore, setMatchScore] = useState(null);
  const debounceRef = useRef(null);
  const lastMatchedRef = useRef('');

  // Trigger Matching bei Änderungen an Name/Klasse
  useEffect(() => {
    // Key für diese Kombination
    const matchKey = `${data.vorname}|${data.nachname}|${data.klasse}`;

    // Nicht erneut matchen wenn sich nichts geändert hat
    if (matchKey === lastMatchedRef.current) {
      return;
    }

    // Debounce löschen (wichtig: Timer aller vorherigen Effect-Aufrufe clearen)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const vorname = data.vorname?.trim() || '';
    const nachname = data.nachname?.trim() || '';
    const klasse = data.klasse || '';

    // Nichts zu matchen
    if (!vorname && !nachname) {
      setMatchScore(null);
      return;
    }

    // Mindestens 2 Zeichen
    if ((vorname.length + nachname.length) < 2) {
      setMatchScore(null);
      return;
    }

    // Debounced API-Call
    debounceRef.current = setTimeout(async () => {
      // Mark this request as being processed
      lastMatchedRef.current = matchKey;
      setMatching(true);

      try {
        const result = await matchStudent(vorname, nachname, klasse);

        // IMPORTANT: Only update state if this response is still relevant
        // Check if the user has changed the input since we made this request
        // (lastMatchedRef gets overwritten when a NEW request starts)
        if (lastMatchedRef.current !== matchKey) {
          // A new request has been started, ignore this stale response
          console.log('Ignoring stale matching response - input changed');
          return;
        }

        if (result.found && result.best_match) {
          setMatchScore(result.best_match.score);
          // matchedStudent und alternatives speichern (alternatives nicht anzeigen, aber für SeaTable)
          onChange(prev => ({
            ...prev,
            matchedStudent: result.best_match,
            alternatives: result.alternatives || [],
          }));
        } else {
          setMatchScore(0);
          onChange(prev => ({
            ...prev,
            matchedStudent: null,
            alternatives: result.alternatives || [],
          }));
        }
      } catch (err) {
        console.error('Matching error:', err);
        setMatchScore(null);
      } finally {
        // Only clear matching state if this is still the current request
        if (lastMatchedRef.current === matchKey) {
          setMatching(false);
        }
      }
    }, 500);

    return () => {
      // Cleanup: debounced Timer clearen, falls noch läuft
      const timerToClear = debounceRef.current;
      if (timerToClear) {
        clearTimeout(timerToClear);
        debounceRef.current = null;
      }
    };
  }, [data.vorname, data.nachname, data.klasse, onChange]);

  const handleChange = (field, value) => {
    onChange(prev => ({ ...prev, [field]: value }));

    // Error für dieses Feld löschen
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Status-Anzeige basierend auf Score (ohne Namen zu zeigen)
  const getStatusMessage = () => {
    if (matching) return null;
    if (matchScore === null) return null;

    if (matchScore >= 90) {
      return { type: 'success', text: 'Schüler gefunden' };
    } else if (matchScore >= 70) {
      return { type: 'warning', text: 'Schüler vermutlich gefunden - bitte eventuell die Schreibweise des Vornamens oder des Nachnamens korrigieren' };
    } else {
      return { type: 'secondary', text: 'Schüler nicht eindeutig gefunden - Bestellung trotzdem möglich' };
    }
  };

  const status = getStatusMessage();

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">Persönliche Daten Schüler/Eltern</h5>
      </div>
      <div className="card-body">
        <div className="row g-3">
          {/* Vorname */}
          <div className="col-md-4">
            <label htmlFor="vorname" className="form-label">
              Vorname des Kindes <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.vorname ? 'is-invalid' : ''}`}
              id="vorname"
              aria-invalid={errors.vorname ? 'true' : 'false'}
              aria-describedby={errors.vorname ? 'vorname-error' : undefined}
              value={data.vorname}
              onChange={(e) => handleChange('vorname', e.target.value)}
              placeholder="z.B. Max"
              autoComplete="off"
            />
            {errors.vorname && (
              <div id="vorname-error" className="invalid-feedback">{errors.vorname}</div>
            )}
          </div>

          {/* Nachname */}
          <div className="col-md-4">
            <label htmlFor="nachname" className="form-label">
              Nachname des Kindes <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.nachname ? 'is-invalid' : ''}`}
              id="nachname"
              aria-invalid={errors.nachname ? 'true' : 'false'}
              aria-describedby={errors.nachname ? 'nachname-error' : undefined}
              value={data.nachname}
              onChange={(e) => handleChange('nachname', e.target.value)}
              placeholder="z.B. Mustermann"
              autoComplete="off"
            />
            {errors.nachname && (
              <div id="nachname-error" className="invalid-feedback">{errors.nachname}</div>
            )}
          </div>

          {/* Klasse */}
          <div className="col-md-4">
            <label htmlFor="klasse" className="form-label">
              Klasse <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${errors.klasse ? 'is-invalid' : ''}`}
              id="klasse"
              aria-invalid={errors.klasse ? 'true' : 'false'}
              aria-describedby={errors.klasse ? 'klasse-error' : undefined}
              value={data.klasse}
              onChange={(e) => handleChange('klasse', e.target.value)}
            >
              <option value="">Bitte wählen...</option>
              {klassen?.map((k) => (
                <option key={k} value={k}>
                  Klasse {k}
                </option>
              ))}
            </select>
            {errors.klasse && (
              <div id="klasse-error" className="invalid-feedback">{errors.klasse}</div>
            )}
          </div>
        </div>

        {/* Status-Anzeige (ohne Namen) */}
        {matching && (
          <div className="mt-3">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span className="visually-hidden">Prüfe...</span>
            </div>
            <span className="text-muted">Prüfe Eingabe...</span>
          </div>
        )}

        {status && (
          <div className={`mt-3 alert alert-${status.type} mb-0 py-2`}>
            <small>
              {status.type === 'success' && '✓ '}
              {status.type === 'warning' && '⚠ '}
              {status.text}
            </small>
          </div>
        )}

        {/* Elterndaten */}
        <div className="row g-3 mt-2">
          <div className="col-md-6">
            <label htmlFor="elternname" className="form-label">
              Name der Eltern <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.elternname ? 'is-invalid' : ''}`}
              id="elternname"
              aria-invalid={errors.elternname ? 'true' : 'false'}
              aria-describedby={errors.elternname ? 'elternname-error' : undefined}
              value={data.elternname}
              onChange={(e) => handleChange('elternname', e.target.value)}
              placeholder="Vor- und Nachname"
              autoComplete="off"
            />
            {errors.elternname && (
              <div id="elternname-error" className="invalid-feedback">{errors.elternname}</div>
            )}
          </div>

          <div className="col-md-6">
            <label htmlFor="email" className="form-label">
              E-Mail-Adresse <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              id="email"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
              value={data.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="ihre@email.de"
              autoComplete="off"
            />
            {errors.email && (
              <div id="email-error" className="invalid-feedback">{errors.email}</div>
            )}
            <div className="form-text">
              An diese Adresse wird die Bestellbestätigung gesendet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentInput;
